import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchTopLinks,
  clearTopLinks,
  fetchTopLinksCsv,
  clearFetchTopLinksCsv,
} from 'redux/analytics/actions';

import { Loader, Checkbox } from 'semantic-ui-react';

import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsTopLinksDetails from 'components/dashboard/analytics-content/AnalyticsTopLinksDetails';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import DownloadButton from 'components/elements/buttons/DownloadButton';

import { topLinks as topLinksFakeData }
  from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

import { performDownload } from 'utils/common';

import './AnalyticsTopLinks.css';

const AnalyticsTopLinks = ({
  date,
  fetchTopLinks,
  clearTopLinks,
  fetchTopLinksCsv,
  clearFetchTopLinksCsv,
  topLinks,
  topLinksError,
  topLinksCsv,
  me,
}) => {
  const { t } = useTranslation();
  const [showDeletedLinks, handleDeletedLinks] = useState(false);
  const [showGraph, handleShowGraph] = useState(false);
  const [activeElementId, handleActiveElementId] = useState(undefined);

  useEffect(() => {
    fetchTopLinks(date, showDeletedLinks);
    return () => clearTopLinks();
  }, [date, showDeletedLinks]);

  useEffect(() => {
    if (topLinksCsv) {
      const date = new Date();
      performDownload(
        `Link_Clicks_List_for_${me.username}_${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}.csv`,
        topLinksCsv,
      );
      setTimeout(() => {
        clearFetchTopLinksCsv();
      }, 3000);
    }
  }, [topLinksCsv]);

  const _handleDeletedLinks = (_e, { checked }) => {
    handleDeletedLinks(!checked);
  };

  const _handleShowGraph = id => {
    handleActiveElementId(id);
    if (activeElementId === id) {
      handleActiveElementId(undefined);
      handleShowGraph(false);
    } else {
      handleShowGraph(true);
    }
  };

  const renderTableData = (topLinksData) => {
    if (!topLinksData) {
      return null;
    }

    return (
      <table>
        <thead>
          <tr>
            <th className="link-number">{t("#")}</th>
            <th>{t("Type")}</th>
            <th>{t("Name")}</th>
            <th />
            <th className="clicks">{t("Clicks")}</th>
            <th />
          </tr>
        </thead>

        <tbody>
          {topLinksData.map((i, index) => (
            <Fragment key={`fragment_top_links_table_row_key_${i.link__type}_${i.link__id}`}>
              {i.link__is_deleted ?
              <>
                <tr
                  key={`top_links_table_row_key_${i.link__type}_${i.link__id}`}
                  className={`deleted ${`show_graph_id_${i.link__type}_${i.link__id}` === activeElementId ? 'expanded' : ''}`}
                >
                  <td className="link-number">{index + 1}.</td>
                  <td className="link-type">
                    <span>{i.link__type.replace('_', ' ')}</span>
                  </td>
                  <td className="link-name">
                    <span>{i.link__name}</span>
                  </td>
                  <td className="deleted-label">{t("this link is deleted")}</td>
                  <td className="clicks">{i.clicks}</td>
                  <td
                      onClick={(e) => _handleShowGraph(e.target.id)}
                      id={`show_graph_id_${i.link__type}_${i.link__id}`}
                      className={`show-graph ${`show_graph_id_${i.link__type}_${i.link__id}` === activeElementId ? 'hide' : ''}`}
                    >
                      {(showGraph && `show_graph_id_${i.link__type}_${i.link__id}` === activeElementId) ?
                        t("Hide Graph")
                        :
                        t("Show Graph")
                      }
                    </td>
                </tr>
                {showGraph && `show_graph_id_${i.link__type}_${i.link__id}` === activeElementId &&
                  <tr>
                    <td colSpan='5'>
                      <AnalyticsTopLinksDetails
                        linkId={i.link__id}
                        linkType={i.link__type}
                        date={date}
                      />
                    </td>
                  </tr>
                }
              </>
                :
                <>
                  <tr
                    key={`top_links_table_row_key_${i.link__type}_${i.link__id}`}
                    className={`${`show_graph_id_${i.link__type}_${i.link__id}` === activeElementId ? 'expanded' : ''}`}
                  >
                    <td className="link-number">{index + 1}.</td>
                    <td className="link-type">
                      <span>{i.link__type.replace('_', ' ')}</span>
                    </td>
                    <td className="link-name">
                      <span>{i.link__name}</span>
                    </td>
                    <td />
                    <td className="clicks">{i.clicks}</td>
                    <td
                      onClick={(e) => _handleShowGraph(e.target.id, showGraph)}
                      id={`show_graph_id_${i.link__type}_${i.link__id}`}
                      className={`show-graph ${`show_graph_id_${i.link__type}_${i.link__id}` === activeElementId ? 'hide' : ''}`}
                    >
                      {(showGraph && `show_graph_id_${i.link__type}_${i.link__id}` === activeElementId) ?
                        t("Hide Graph")
                        :
                        t("Show Graph")
                      }
                    </td>
                  </tr>
                  {showGraph && `show_graph_id_${i.link__type}_${i.link__id}` === activeElementId &&
                    <tr>
                      <td colSpan='5'>
                        <AnalyticsTopLinksDetails
                          linkId={i.link__id}
                          linkType={i.link__type}
                          date={date}
                        />
                      </td>
                    </tr>
                  }
                </>
              }
            </Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  if (topLinksError) {
    return null;
  }

  if (topLinks && topLinks.length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("My Top Links")} text={t("Looks like you have no data to display yet…")} />;
  }

  return (
    <AnalyticsSection
      className="analytics-top-links__component"
      header={
        <>
          <div className="title">
            <h3>{t("My Top Links")}</h3>
          </div>
          {me.subscription.ff_advanced_analytics &&
            <Checkbox
              label={t("Hide Deleted Links")}
              onChange={_handleDeletedLinks}
              checked={!showDeletedLinks}
            />
          }
        </>
      }
    >
      <div className="analytics-top-links-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderTableData(topLinksFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderTableData(topLinks)}
            {topLinks && topLinks.length > 0 &&
              <DownloadButton onClick={() => { fetchTopLinksCsv(date); }}>
                {t("Export to CSV")}
              </DownloadButton>
            }
          </>
        }
        <Loader active={!topLinks} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsTopLinks.propTypes = {
  fetchTopLinksCsv: PropTypes.func.isRequired,
  clearFetchTopLinksCsv: PropTypes.func.isRequired,
  fetchTopLinks: PropTypes.func.isRequired,
  clearTopLinks: PropTypes.func.isRequired,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  topLinks: PropTypes.array,
  topLinksError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
  topLinksCsv: PropTypes.object,
};

AnalyticsTopLinks.defaultProps = {
  topLinks: null,
  topLinksError: null,
  topLinksCsv: null,
};

const mapStateToProps = state => ({
  me: state.usersReducer.me,
  topLinks: state.analyticsReducer.topLinks,
  topLinksError: state.analyticsReducer.topLinksError,
  topLinksCsv: state.analyticsReducer.topLinksCsv,
});

export default connect(
  mapStateToProps,
  {
    fetchTopLinks,
    clearTopLinks,
    fetchTopLinksCsv,
    clearFetchTopLinksCsv,
  },
)(AnalyticsTopLinks);
